import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Flag, History, UserPlus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { EmptyState, ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { useToast } from '@/providers/UiProviders'
import { fetchAllTickets, type AdminTicket, type AdminStaff } from '@/lib/adapters'
import {
  safeList,
  assignTicket,
  setTicketPriority,
  TICKET_PRIORITIES,
  TK_STR as S,
} from './ticketsApi'

const isActive = (t: AdminTicket) => t.status === 'open' || t.status === 'pending'
const tStatus = (s: string) => (s === 'open' ? 'open' : s === 'pending' ? 'pending' : 'deleted')

/** Shared loader: barcha tiketlar (JONLI ≤300). */
function useTickets() {
  const [rows, setRows] = useState<AdminTicket[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchAllTickets(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])
  return { rows, err, reload }
}

/** A7.08 — Yozishmalar tarixi. Barcha tiketlar oxirgi faollik bo'yicha; karta = to'liq yozishma. */
export function CorrespondenceHistory() {
  const { rows, err, reload } = useTickets()
  const [q, setQ] = useState('')
  const list = useMemo(() => {
    const ql = q.trim().toLowerCase()
    return [...(rows ?? [])]
      .filter((t) => !ql || `${t.subject} ${t.username ?? ''} ${t.category}`.toLowerCase().includes(ql))
      .sort((a, b) => b.updated_at - a.updated_at)
  }, [rows, q])

  return (
    <>
      <PageHeader title={S.history} screenId="A7.08" description={S.historyNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-3xl space-y-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={S.subject}
            className="w-full rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : list.length === 0 ? (
            <EmptyState title={S.empty} icon={<History size={28} strokeWidth={1.5} />} />
          ) : (
            <div className="divide-y divide-border-subtle rounded-lg border border-border-default bg-surface-2">
              {list.map((t) => (
                <Link key={t.id} to={`/support/${t.id}`} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-surface-3">
                  <StatusBadge status={tStatus(t.status)} />
                  <span className="min-w-0 flex-1 truncate text-primary">{t.subject || `#${t.id}`}</span>
                  <span className="shrink-0 text-xs text-tertiary">{t.username ? `@${t.username}` : `#${t.user_id}`}</span>
                  <RelativeTime value={t.updated_at} className="w-24 shrink-0 text-right text-xs text-secondary" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/** A7.09 — Tayinlash va navbat. Tiketlar (JONLI) + agentga biriktirish (greenfield POST). */
export function AssignmentQueue() {
  const { rows, err, reload } = useTickets()
  const { toast } = useToast()
  const [staff, setStaff] = useState<AdminStaff[]>([])
  const [busy, setBusy] = useState<number | null>(null)
  const [sel, setSel] = useState<Record<number, string>>({})

  useEffect(() => {
    const ac = new AbortController()
    safeList<AdminStaff>('/admin/staff', 'staff', ac.signal).then(setStaff)
    return () => ac.abort()
  }, [])

  const active = useMemo(() => (rows ?? []).filter(isActive), [rows])
  const nameOf = (uid?: number | null) => staff.find((s) => s.id === uid)?.username

  const doAssign = async (t: AdminTicket) => {
    const raw = sel[t.id]
    if (raw === undefined) return
    setBusy(t.id)
    try {
      await assignTicket(t.id, raw === '' ? null : Number(raw))
      toast(S.assignDone, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title={S.assign} screenId="A7.09" description={S.assignNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !rows ? (
          <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : active.length === 0 ? (
          <EmptyState title={S.empty} icon={<UserPlus size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="divide-y divide-border-subtle rounded-lg border border-border-default bg-surface-2">
            {active.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <Link to={`/support/${t.id}`} className="min-w-0 flex-1 truncate text-primary hover:underline">
                  #{t.id} · {t.subject || '—'}
                </Link>
                <span className="shrink-0 text-xs text-tertiary">
                  {t.assigned_to ? nameOf(t.assigned_to) ?? `#${t.assigned_to}` : S.unassigned}
                </span>
                <select
                  value={sel[t.id] ?? (t.assigned_to != null ? String(t.assigned_to) : '')}
                  onChange={(e) => setSel((m) => ({ ...m, [t.id]: e.target.value }))}
                  className="shrink-0 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-xs text-primary"
                >
                  <option value="">{S.unassigned}</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>{s.username}</option>
                  ))}
                </select>
                <button
                  disabled={busy === t.id}
                  onClick={() => doAssign(t)}
                  className="shrink-0 rounded-md border border-accent bg-accent-subtle px-2.5 py-1 text-xs text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                >
                  {S.assignTo}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

const PR_TONE: Record<string, string> = {
  urgent: 'text-danger',
  high: 'text-warning',
  normal: 'text-secondary',
  low: 'text-tertiary',
}

/** A7.10 — Prioritet boshqaruvi. Tiketlar (JONLI) muhimlik bo'yicha; o'zgartirish greenfield POST. */
export function PriorityBoard() {
  const { rows, err, reload } = useTickets()
  const { toast } = useToast()
  const [busy, setBusy] = useState<number | null>(null)

  const active = useMemo(() => (rows ?? []).filter(isActive), [rows])
  const label = (p: string) => (S as Record<string, string>)[p] ?? p

  const change = async (t: AdminTicket, priority: string) => {
    if (priority === t.priority) return
    setBusy(t.id)
    try {
      await setTicketPriority(t.id, priority)
      toast(S.priorityDone, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title={S.priorityBoard} screenId="A7.10" description={S.priorityNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !rows ? (
          <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : active.length === 0 ? (
          <EmptyState title={S.empty} icon={<Flag size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="divide-y divide-border-subtle rounded-lg border border-border-default bg-surface-2">
            {active.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <Link to={`/support/${t.id}`} className="min-w-0 flex-1 truncate text-primary hover:underline">
                  #{t.id} · {t.subject || '—'}
                </Link>
                <span className={`shrink-0 text-xs font-medium ${PR_TONE[t.priority] ?? 'text-secondary'}`}>
                  {label(t.priority || 'normal')}
                </span>
                <select
                  value={t.priority || 'normal'}
                  disabled={busy === t.id}
                  onChange={(e) => change(t, e.target.value)}
                  className="shrink-0 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-xs text-primary disabled:opacity-50"
                >
                  {TICKET_PRIORITIES.map((p) => (
                    <option key={p} value={p}>{label(p)}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
